import React from 'react';
import isEqual from 'lodash.isequal';

export const DataContext = React.createContext();

let tempDate = new Date();
tempDate.setDate(tempDate.getDate() - 1);
const yesterday = tempDate;
tempDate = new Date();
tempDate.setDate(tempDate.getDate() - 7);
const weekAgo = tempDate;

const intervals = {d: 'days', w: 'weeks', m: 'months', q: 'quarters', y: 'years'}

let lastDateQuery = {
  rows: null,
  startDate: null,
  endDate: null,
  articles: [],
  authors: [],
  teams: [],
  products: [],
  languages: [],
  humanLanguages: [],
}
let dateQueryInProgress = false;
let lastItemQuery = lastDateQuery;
let itemQueryInProgress = false;
let dateController = null;
let itemController = null;

let articles = {};
let teams = {};
let authors = {};
let products = {};
let languages = {};
let humanLanguages = {};
let lists = {};

export default function DataContextProvider({children}) {
  const [blog, setBlog] = React.useState('twilio');
  const [dateRange, setDateRange] = React.useState([weekAgo, yesterday, 'd'])
  const [globalFilters, setGlobalFilters] = React.useState({});
  const [split, setSplit] = React.useState('');
  const [pubRange, setPubRange] = React.useState([null, null]);
  const [dateData, setDateData] = React.useState(null);
  const [itemData, setItemData] = React.useState(null);
  const [selectedMetric, setSelectedMetric] = React.useState('1');
  const [ignoreNone, setIgnoreNone] = React.useState(true);
  const [dummy, setDummy] = React.useState(0);

  const filtersToQueryString = filters => {
    if (!filters)
      return '';
    let q = '';
    if (filters.articles) {
      if (q !== '')
        q += '&';
      q += 'article=' + filters.articles.join('&article=');
    }
    if (filters.products) {
      if (q !== '')
        q += '&';
      q += 'product=' + filters.products.join('&product=');
      if (filters.productsInverted)
        q += '&invert_products=true';
    }
    if (filters.teams) {
      if (q !== '')
        q += '&';
      q += 'team=' + filters.teams.join('&team=');
      if (filters.teamsInverted)
        q += '&invert_teams=true';
    }
    if (filters.authors) {
      if (q !== '')
        q += '&';
      q += 'author=' + filters.authors.join('&author=');
      if (filters.authorsInverted)
        q += '&invert_authors=true';
    }
    if (filters.languages) {
      if (q !== '')
        q += '&';
      q += 'language=' + filters.languages.join('&language=');
      if (filters.languagesInverted)
        q += '&invert_languages=true';
    }
    if (filters.humanLanguages) {
      if (q !== '')
        q += '&';
      q += 'human_language=' + filters.humanLanguages.join('&human_language=');
      if (filters.humanLanguagesInverted)
        q += '&invert_human_languages=true';
    }
    if (filters.human_languages) {
      if (q !== '')
        q += '&';
      q += 'human_language=' + filters.human_languages.join('&human_language=');
      if (filters.humanLanguagesInverted)
        q += '&invert_human_languages=true';
    }
    if (filters.countries) {
      if (q !== '')
        q += '&';
      let c = []
      for (let i = 0; i < filters.countries.length; i++) {
        if (filters.countries[i].indexOf(',') === -1) {
          c.push(filters.countries[i]);
        }
        else {
          c = c.concat(filters.countries[i].split(','));
        }
      }
      q += 'country=' + c.join('&country=');
    }
    if (filters.article_months) {
      if (q !== '')
        q += '&';
      q += 'article_month=' + filters.article_months.join('&article_month=');
    }
    if (filters.article_quarters) {
      if (q !== '')
        q += '&';
      q += 'article_quarter=' + filters.article_quarters.join('&article_quarter=');
    }
    if (filters.article_years) {
      if (q !== '')
        q += '&';
      q += 'article_year=' + filters.article_years.join('&article_year=');
    }
    if (filters.lists) {
      if (q !== '')
        q += '&';
      q += 'list=' + filters.lists.join('&list=');
    }
    if (filters.splitTranslations) {
      if (q !== '')
        q += '&';
      q += 'split_translations=1';
    }
    return (q !== '') ? '&' + q : '';
  };

  const buildQueryString = (query, {lastYear} = {}) => {
    let fromDate = new Date(query.start || dateRange[0]).toISOString().substring(0, 10);
    let toDate = new Date(query.end || dateRange[1]).toISOString().substring(0, 10);
    if (lastYear) {
      fromDate = (fromDate.substring(0, 4) - 1) + fromDate.substring(4);
      toDate = (toDate.substring(0, 4) - 1) + toDate.substring(4);
    }
    let queryString = filtersToQueryString({...globalFilters, ...(query.filters || {})});
    if (pubRange[0] !== null) {
      let fromPubDate = pubRange[0].toISOString().substring(0, 10);
      let toPubDate = pubRange[1].toISOString().substring(0, 10);
      queryString += `&pub_from=${fromPubDate}&pub_to=${toPubDate}`;
    }
    if (query.split && split !== 'source') {
      queryString += `&split=${query.split}`;
    }
    return `blog=${blog}&start=${fromDate}&end=${toDate}${queryString}`;
  };

  const dataContext = {
    blog: blog,
    start: dateRange[0],
    end: dateRange[1],
    interval: dateRange[2],
    globalFilters: globalFilters,
    split: split,
    pubStart: pubRange[0],
    pubEnd: pubRange[1],
    dateData: dateData,
    itemData: itemData,
    selectedMetric: selectedMetric,
    ignoreNone: ignoreNone,
    dummy: dummy,  // only used to force full-screen updates

    forceUpdate: () => {
      lastDateQuery = {};
      lastItemQuery = {};
      setDummy(dummy + 1);
    },
    setBlog: (blog) => { setBlog(blog); },
    setRange: (start, end) => { setDateRange(r => [start, end, r[2]]) },
    setSplit: (split) => { setSplit(split); },
    setPubRange: (start, end) => setPubRange([start, end]),
    setInterval: (interval) => { setDateRange(r => [r[0], r[1], interval]) },
    setGlobalFilters: (filters) => { setGlobalFilters(filters); },
    getDateData: (query) => {
      if (!isEqual(query, lastDateQuery)) {
        if (dateController) {
          dateController.abort();
          dateController = null;
        }
        dateQueryInProgress = true;
        (async () => {
          try {
            const interval = query.interval || dateRange[2];
            setDateData(null);
            lastDateQuery = query;
            let queryString = buildQueryString(query);
            dateController = new AbortController();
            let response = await fetch(`/api/query/${intervals[interval]}?${queryString}`, {signal: dateController.signal});
            let result = await response.json();

            // also get previous year, for YoY results
            queryString = buildQueryString(query, {lastYear: true});
            response = await fetch(`/api/query/${intervals[interval]}?${queryString}`)
            let resultLastYear = await response.json();

            dateQueryInProgress = false;
            setDateData({current: result, lastYear: resultLastYear});
          } catch (error) {
            // Silently ignore abort errors
            if (error.name !== 'AbortError') {
              console.error('Error fetching date data:', error);
            }
            dateQueryInProgress = false;
          }
        })();
        return false;
      }
      return !dateQueryInProgress;
    },
    getItemData: (query) => {
      if (!isEqual(query, lastItemQuery)) {
        if (itemController) {
          itemController.abort();
          itemController = null;
        }
        itemQueryInProgress = true;
        (async () => {
          try {
            setItemData(null);
            lastItemQuery = query;
            let queryString = buildQueryString(query);
            itemController = new AbortController();
            let response = await fetch(`/api/query/${query.rows}?${queryString}`, {signal: itemController.signal})
            let result = await response.json();
            itemQueryInProgress = false;
            setItemData(result);
          } catch (error) {
            // Silently ignore abort errors
            if (error.name !== 'AbortError') {
              console.error('Error fetching item data:', error);
            }
            itemQueryInProgress = false;
          }
        })();
        return false;
      }
      return !itemQueryInProgress;
    },
    setSelectedMetric: setSelectedMetric,
    toggleNone: () => setIgnoreNone(n => !n),
  };

  return (
    <DataContext.Provider value={dataContext}>
      {children}
    </DataContext.Provider>
  )
}

export async function getArticle(id) {
  if (id in articles) {
    return articles[id];
  }
  let response = await fetch(`/api/articles/${id}`);
  let result = await response.json();
  articles[id] = result;
  return result;
}

export async function getTeam(id) {
  if (id in teams) {
    return teams[id];
  }
  let response = await fetch(`/api/teams/${id}`);
  let result = await response.json();
  teams[id] = result;
  return result;
}

export async function getAuthor(id) {
  if (id in authors) {
    return authors[id];
  }
  let response = await fetch(`/api/authors/${id}`);
  let result = await response.json();
  authors[id] = result;
  return result;
}

export async function getProduct(id) {
  if (id in products) {
    return products[id];
  }
  let response = await fetch(`/api/products/${id}`);
  let result = await response.json();
  products[id] = result;
  return result;
}

export async function getLanguage(id) {
  if (id in languages) {
    return languages[id];
  }
  let response = await fetch(`/api/languages/${id}`);
  let result = await response.json();
  languages[id] = result;
  return result;
}

export async function getHumanLanguage(id) {
  if (id in humanLanguages) {
    return humanLanguages[id];
  }
  let response = await fetch(`/api/human_languages/${id}`);
  let result = await response.json();
  humanLanguages[id] = result;
  return result;
}

export async function getList(id) {
  if (id in lists) {
    return lists[id];
  }
  let response = await fetch(`/api/lists/${id}`);
  let result = await response.json();
  lists[id] = result;
  return result;
}

export async function resetList(id) {
  if (id in lists) {
    delete lists[id];
  }
}
